<mat-progress-bar *ngIf="(loading && !data) || coverageLoading()" mode="indeterminate"></mat-progress-bar>
<p *ngIf="refreshing" class="refresh-hint">Refreshing role data…</p>

<button mat-stroked-button type="button" (click)="load(true)" [disabled]="isBusy" style="margin-bottom: 0.75rem">Reload</button>
<button mat-stroked-button type="button" (click)="reviewEvidenceModes()" [disabled]="isBusy || !slug" style="margin-bottom: 0.75rem; margin-left: 0.5rem">Review evidence modes (AI)</button>
<button
  mat-flat-button
  color="primary"
  type="button"
  (click)="batchGenerateFullyAiLessons()"
  [disabled]="isBusy || !slug || !isRoleActive"
  style="margin-bottom: 0.75rem; margin-left: 0.5rem"
  title="Queue fully AI lessons for all active profiles and specializations (draft until published)">
  <mat-icon>auto_awesome</mat-icon>
  Generate fully AI lessons (role)
</button>
<button
  mat-stroked-button
  type="button"
  (click)="backfillLessonTargetProficiency()"
  [disabled]="isBusy || !slug"
  style="margin-bottom: 0.75rem; margin-left: 0.5rem"
  title="AI-classify missing targetProficiency on published lessons for this role (chunks of 500, auto-continues)">
  <mat-icon>psychology</mat-icon>
  Backfill lesson proficiency (AI)
</button>
<mat-card *ngIf="previewLesson() as preview" class="section-card" style="margin-bottom: 1rem">
  <app-admin-canonical-lesson-preview
    [lesson]="preview"
    [busy]="isBusy || !!packActionBusyId()"
    (publish)="publishPreviewLesson($event)"
    (archive)="archivePreviewLesson($event)"
    (close)="closeLessonPreview()">
  </app-admin-canonical-lesson-preview>
</mat-card>

<mat-card class="coverage-card section-card">
  <div class="coverage-header">
    <div>
      <h2>Verified coverage</h2>
      <p class="hint">Per-topic Mapped, Published, and Verified counts for this role. Gap-fill uses the publish threshold below.</p>
    </div>
    <span class="badge" *ngIf="coverageRole as role">
      {{ coveredCoverageTopics(role) }}/{{ totalCoverageTopics(role) }} topics ({{ coveragePercent(role) }}%)
    </span>
  </div>
  <form [formGroup]="coverageForm" class="coverage-form">
    <mat-form-field appearance="outline">
      <mat-label>Target verified / topic</mat-label>
      <input matInput type="number" formControlName="targetThreshold" />
    </mat-form-field>
    <mat-form-field appearance="outline">
      <mat-label>Publish threshold</mat-label>
      <input matInput type="number" step="0.01" formControlName="publishThreshold" />
    </mat-form-field>
    <button
      mat-flat-button
      type="button"
      color="primary"
      (click)="loadCoverageReport()"
      [disabled]="coverageForm.invalid || coverageLoading() || isBusy">
      Refresh coverage
    </button>
    <mat-checkbox
      [checked]="gapsOnly()"
      (change)="setGapsOnly($event.checked)"
      [disabled]="coverageLoading()">
      Gaps only
    </mat-checkbox>
    <mat-checkbox
      [checked]="missingLessonsOnly()"
      (change)="setMissingLessonsOnly($event.checked)"
      [disabled]="coverageLoading()">
      Missing lessons only
    </mat-checkbox>
    <mat-checkbox
      [checked]="missingFullyAiLessonsOnly()"
      (change)="setMissingFullyAiLessonsOnly($event.checked)"
      [disabled]="coverageLoading()">
      Missing fully AI lessons
    </mat-checkbox>
  </form>
</mat-card>

<mat-card *ngIf="lessonsRequiringReview().length || reviewLessonsLoading()" class="review-card" style="margin-bottom: 1rem">
  <h2>Lessons requiring review</h2>
  <p class="meta subtle">Published lessons flagged by learner “not helpful” feedback. False alarm suppresses further flags for that version.</p>
  <mat-progress-bar *ngIf="reviewLessonsLoading()" mode="indeterminate"></mat-progress-bar>
  <div *ngFor="let lesson of lessonsRequiringReview()" class="review-row" style="display: flex; gap: 0.75rem; align-items: center; margin-top: 0.5rem">
    <div style="flex: 1">
      <strong>{{ lesson.topicName || lesson.skillId }}</strong>
      <span class="meta"> · v{{ lesson.version }} · {{ lesson.requiresReviewIssueCode || 'feedback' }}</span>
    </div>
    <button mat-stroked-button type="button" (click)="markLessonFalseAlarm(lesson)" [disabled]="isBusy">
      False alarm
    </button>
  </div>
</mat-card>

<mat-card *ngIf="abComparisonFacets().length || abComparisonsLoading()" class="ab-signals-card" style="margin-bottom: 1rem">
  <h2>Lesson A/B signals</h2>
  <p class="meta subtle">
    Facets with more than one published pack and more than {{ abComparisonsMinServes() }} serves.
    Compare pack metrics, then archive a weak arm or regenerate it from the same sources.
  </p>
  <mat-progress-bar *ngIf="abComparisonsLoading()" mode="indeterminate"></mat-progress-bar>
  <mat-accordion *ngIf="abComparisonFacets().length" multi>
    <mat-expansion-panel *ngFor="let facet of abComparisonFacets()">
      <mat-expansion-panel-header>
        <mat-panel-title>
          {{ facet.skillName }}{{ facet.topicName ? ' · ' + facet.topicName : '' }}
        </mat-panel-title>
        <mat-panel-description>
          {{ facet.packs.length }} packs · {{ facet.serveCount }} serves
        </mat-panel-description>
      </mat-expansion-panel-header>
      <div class="ab-pack-grid">
        <div class="ab-pack-card" *ngFor="let pack of facet.packs">
          <div class="ab-pack-header">
            <strong>Pack {{ pack.evidencePackIndex ?? 0 }}</strong>
            <span class="meta">v{{ pack.version }} · {{ pack.status }} · conf {{ (pack.confidenceScore || 0) | number:'1.2-2' }}</span>
            <span class="meta" *ngIf="pack.isFullyAiGenerated" style="color: #b45309"> · Fully AI</span>
          </div>
          <p class="meta subtle" *ngIf="pack.isFullyAiGenerated && pack.riskNote">{{ pack.riskNote }}</p>
          <p class="meta">
            Feedback {{ pack.feedbackSummary?.feedbackCount ?? 0 }}
            · avg {{ (pack.feedbackSummary?.averageRating ?? 0) | number:'1.1-1' }}
            · helpful {{ pack.feedbackSummary?.helpfulVotes ?? 0 }}
            · not helpful {{ pack.feedbackSummary?.notHelpfulVotes ?? 0 }}
          </p>
          <p class="meta">
            Complete {{ pack.feedbackSummary?.completionSignals ?? 0 }}
            · drop-off {{ pack.feedbackSummary?.dropOffSignals ?? 0 }}
            · score {{ (pack.engagementSignals?.learnerFeedbackScore ?? 0) | number:'1.2-2' }}
          </p>
          <p class="meta subtle" *ngIf="pack.generationSignals as gen">
            Sources {{ gen.packSize }} · mean rec {{ (gen.meanRecommendationScore || 0) | number:'1.2-2' }}
            · official {{ gen.officialSourceCount }}
          </p>
          <div class="ab-pack-actions">
            <button
              mat-stroked-button
              type="button"
              (click)="openLessonPreview(pack)"
              [disabled]="isBusy">
              Review
            </button>
            <button
              *ngIf="pack.status === 'draft'"
              mat-stroked-button
              type="button"
              color="primary"
              (click)="publishLessonPack(pack)"
              [disabled]="isBusy">
              {{ packActionBusyId() === pack.id ? 'Working…' : 'Publish' }}
            </button>
            <button
              mat-stroked-button
              type="button"
              color="warn"
              (click)="archiveLessonPack(pack)"
              [disabled]="isBusy">
              {{ packActionBusyId() === pack.id ? 'Working…' : 'Archive' }}
            </button>
            <button
              mat-stroked-button
              type="button"
              (click)="regenerateLessonPack(pack)"
              [disabled]="isBusy">
              {{ packActionBusyId() === pack.id ? 'Working…' : 'Regenerate' }}
            </button>
          </div>
        </div>
      </div>
    </mat-expansion-panel>
  </mat-accordion>
  <p *ngIf="!abComparisonsLoading() && !abComparisonFacets().length" class="meta subtle">
    No qualifying A/B facets yet.
  </p>
</mat-card>

<mat-card *ngIf="error" class="error-card">
  <h2>Could not load skills and resources</h2>
  <p>{{ error }}</p>
</mat-card>

<ng-template
  #skillCard
  let-skillItem="skillItem"
  let-level="level"
  let-specializationSlug="specializationSlug">
  <mat-card class="skill-card" *ngIf="skillHasVisibleTopics(skillItem) || (!gapsOnly() && !missingLessonsOnly()) || coverageByTopic().size === 0">
    <header class="skill-card-header">
      <div class="skill-title-row">
        <h3>{{ skillItem.skill.name }}</h3>
        <div class="skill-actions">
          <button mat-icon-button type="button" (click)="openRenameSkillDialog(skillItem.skill.id, skillItem.skill.name)" [disabled]="loading" title="Rename skill">
            <mat-icon>edit</mat-icon>
          </button>
          <button mat-icon-button type="button" (click)="openEditSkillDependenciesDialog(skillItem, level, specializationSlug)" [disabled]="loading" title="Edit skill dependencies">
            <mat-icon>account_tree</mat-icon>
          </button>
          <button mat-icon-button type="button" (click)="openAddTopicDialog(skillItem, level, specializationSlug)" [disabled]="loading" title="Add topic">
            <mat-icon>playlist_add</mat-icon>
          </button>
          <button
            mat-icon-button
            type="button"
            (click)="populateSkillResources(skillItem)"
            [disabled]="loading || !resolveTopics(skillItem).length"
            title="Populate skill resources">
            <mat-icon>inventory_2</mat-icon>
          </button>
          <button
            mat-icon-button
            type="button"
            (click)="validateSkillResources(skillItem)"
            [disabled]="isBusy || coverageForm.invalid || validatingSkillId() === skillItem.skill.id"
            title="Revalidate skill resources">
            <mat-icon>fact_check</mat-icon>
          </button>
          <button mat-icon-button type="button" (click)="removeSkill(skillItem, level, specializationSlug)" [disabled]="loading" title="Remove from profile">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
      </div>
      <div class="skill-category-row">
        <mat-chip-set>
          <mat-chip>{{ skillItem.category }}</mat-chip>
          <mat-chip *ngIf="skillItem.isFundamental" color="accent" highlighted>Fundamental</mat-chip>
        </mat-chip-set>
        <button mat-icon-button type="button" (click)="openUpdateCategoryDialog(skillItem.skill.id, skillItem.category)" [disabled]="loading" title="Edit category (catalog)">
          <mat-icon>label</mat-icon>
        </button>
      </div>
      <div class="skill-fundamental-row">
        <mat-checkbox
          [checked]="!!skillItem.isFundamental"
          (change)="toggleFundamental(skillItem, level, specializationSlug)"
          [disabled]="loading"
          title="Lock this skill onto career challenges for this role">
          Fundamental skill
        </mat-checkbox>
      </div>
    </header>
    <p class="meta">Depends on: {{ formatDependsOn(skillItem) }}</p>
    <p class="meta subtle">Max topic importance: {{ skillItem.importance | number:'1.2-2' }}</p>

    <div class="topic-list">
      <div
        class="topic-row-card"
        *ngFor="let topic of visibleTopics(skillItem); trackBy: trackByTopic"
        [class.gap]="topicCoverage(skillItem.skill.id, topic.name) && !topicCoverage(skillItem.skill.id, topic.name)?.meetsTarget">
        <div
          class="topic-summary"
          role="button"
          tabindex="0"
          (click)="toggleTopic(skillItem.skill.id, topic.name)"
          (keydown.enter)="toggleTopic(skillItem.skill.id, topic.name)"
          (keydown.space)="$event.preventDefault(); toggleTopic(skillItem.skill.id, topic.name)">
          <span class="topic-name">{{ topic.name }}</span>
          <span class="meta">{{ topic.evidenceMode || 'Unclassified evidence' }}</span>
          <ng-container *ngIf="topicCoverage(skillItem.skill.id, topic.name) as cov">
            <mat-chip
              class="coverage-status-chip"
              [style.background-color]="cov.meetsTarget ? '#15803d' : '#b91c1c'"
              style="color:#fff">
              {{ cov.meetsTarget ? 'Covered' : 'Gap' }}
            </mat-chip>
          </ng-container>
          <mat-icon class="expand-icon">{{ isTopicExpanded(skillItem.skill.id, topic.name) ? 'expand_less' : 'expand_more' }}</mat-icon>
        </div>
        <div class="topic-details" *ngIf="isTopicExpanded(skillItem.skill.id, topic.name)">
          <p class="meta topic-meta">{{ topic.level }} · importance {{ topic.importance | number:'1.2-2' }}</p>
          <p class="meta">Depends on: {{ formatTopicDependsOn(topic, level, specializationSlug) }}</p>
          <ng-container *ngIf="topicCoverage(skillItem.skill.id, topic.name) as cov">
            <p class="meta" *ngIf="cov.publishedLessonTargetProficiencies?.length">
              Lesson milestones: {{ cov.publishedLessonTargetProficiencies?.join(', ') }}
            </p>
            <p class="meta" *ngIf="cov.missingLessonTargetProficiencies?.length" style="color:#b91c1c">
              Missing lesson milestones: {{ cov.missingLessonTargetProficiencies?.join(', ') }}
            </p>
          </ng-container>
          <div class="topic-actions">
            <button mat-button type="button" class="count-btn" (click)="toggleResources(skillItem.skill.id, topic.name)" [disabled]="loading">
              resources: {{ topicResourceCount(skillItem, topic.name) }}
              <mat-icon>{{ isResourcesExpanded(skillItem.skill.id, topic.name) ? 'expand_less' : 'expand_more' }}</mat-icon>
            </button>
            <ng-container *ngIf="topicCoverage(skillItem.skill.id, topic.name) as cov">
              <span class="mpv-counts">M {{ cov.mappedResourceCount }} / P {{ cov.publishedResourceCount }} / V {{ cov.verifiedResourceCount }}</span>
              <mat-chip *ngIf="cov.hasCanonicalLesson" class="lesson-chip">Lesson</mat-chip>
              <mat-chip *ngIf="cov.hasFullyAiCanonicalLesson" class="lesson-chip fully-ai-chip">Fully AI</mat-chip>
              <mat-chip *ngIf="cov.requiredProficiency" class="lesson-chip">Target {{ cov.requiredProficiency }}</mat-chip>
            </ng-container>
            <button
              mat-icon-button
              type="button"
              *ngIf="canGapFillTopic(skillItem.skill.id, topic.name)"
              [disabled]="loading || isPopulatingTopic(skillItem.skill.id, topic.name)"
              (click)="populateTopicGapFill(skillItem.skill.id, topic.name)"
              title="Gap-fill topic resources">
              <mat-icon>inventory_2</mat-icon>
            </button>
            <button mat-icon-button type="button" (click)="openEditTopicDialog(skillItem, topic, level, specializationSlug)" [disabled]="loading" title="Edit topic">
              <mat-icon>tune</mat-icon>
            </button>
            <button mat-icon-button type="button" (click)="openRenameTopicDialog(skillItem.skill.id, topic.name)" [disabled]="loading" title="Rename topic">
              <mat-icon>drive_file_rename_outline</mat-icon>
            </button>
            <button
              mat-icon-button
              type="button"
              (click)="generateLesson(skillItem.skill.id, topic.name)"
              [disabled]="loading || topicResourceCount(skillItem, topic.name) === 0"
              [title]="topicResourceCount(skillItem, topic.name) === 0 ? 'No published resources — use Fully AI lesson instead' : 'Generate next evidence pack (max 5 sources)'">
              <mat-icon>auto_stories</mat-icon>
            </button>
            <button
              *ngIf="topicResourceCount(skillItem, topic.name) === 0"
              mat-icon-button
              type="button"
              (click)="generateFullyAiLesson(skillItem.skill.id, topic.name)"
              [disabled]="loading"
              title="Generate fully AI lesson (no curated resources — draft)">
              <mat-icon>psychology</mat-icon>
            </button>
            <button mat-icon-button type="button" (click)="showLessonPacks(skillItem.skill.id, topic.name)" [disabled]="loading" title="Compare lesson pack signals">
              <mat-icon>insights</mat-icon>
            </button>
            <button mat-icon-button type="button" (click)="removeTopic(skillItem, topic.name, level, specializationSlug)" [disabled]="loading" title="Remove topic">
              <mat-icon>remove_circle_outline</mat-icon>
            </button>
          </div>
          <div class="topic-actions" *ngIf="hasMissingLessonTargets(skillItem.skill.id, topic.name)">
            <ng-container *ngFor="let missingTarget of missingLessonTargets(skillItem.skill.id, topic.name)">
              <button
                mat-stroked-button
                type="button"
                (click)="topicResourceCount(skillItem, topic.name) === 0 ? generateFullyAiLesson(skillItem.skill.id, topic.name, missingTarget) : generateLesson(skillItem.skill.id, topic.name, missingTarget)"
                [disabled]="loading || actionBusy()">
                {{ topicResourceCount(skillItem, topic.name) === 0 ? 'Generate fully AI' : 'Generate lesson' }}: {{ missingTarget }}
              </button>
            </ng-container>
          </div>
          <div class="resources" *ngIf="isResourcesExpanded(skillItem.skill.id, topic.name)">
            <p *ngIf="isResourcesLoading(skillItem.skill.id, topic.name)" class="empty">Loading resources…</p>
            <ng-container *ngIf="!isResourcesLoading(skillItem.skill.id, topic.name)">
              <div class="resource-item" *ngFor="let resource of getCachedResources(skillItem.skill.id, topic.name); trackBy: trackByResource">
                <div class="resource-main">
                  <a [href]="resource.url" target="_blank" rel="noopener noreferrer">{{ resource.title }}</a>
                  <p>{{ resource.provider }} · {{ resource.type }}</p>
                </div>
                <button mat-icon-button type="button" (click)="archiveResource(resource, skillItem.skill.id, topic.name)" [disabled]="loading" title="Archive published resource">
                  <mat-icon>archive</mat-icon>
                </button>
              </div>
              <p class="empty" *ngIf="!getCachedResources(skillItem.skill.id, topic.name).length">No resources mapped.</p>
            </ng-container>
          </div>
        </div>
      </div>
      <p class="empty" *ngIf="!visibleTopics(skillItem).length">No topics on this skill.</p>
    </div>
  </mat-card>
</ng-template>

<ng-container *ngIf="data as role">
  <section class="section">
    <h2>Core Skills and Resources</h2>
    <mat-accordion multi>
      <mat-expansion-panel *ngFor="let profile of role.seniorityProfiles" [hidden]="!profileHasVisibleSkills(profile.skills)">
        <mat-expansion-panel-header>
          <mat-panel-title>{{ profile.level }}</mat-panel-title>
          <mat-panel-description>{{ profile.skills.length}} skills</mat-panel-description>
        </mat-expansion-panel-header>
        <div class="profile-actions">
          <button mat-stroked-button type="button" (click)="openAddSkillDialog(profile.level)" [disabled]="loading">
            <mat-icon>add</mat-icon>
            Add skill
          </button>
          <button mat-stroked-button type="button" (click)="openCopySkillsDialog(profile.level)" [disabled]="loading">
            <mat-icon>content_copy</mat-icon>
            Copy skills from…
          </button>
          <button
            mat-stroked-button
            type="button"
            (click)="batchGenerateLessons(profile.level)"
            [disabled]="isBusy || !slug"
            title="Generate canonical lessons for all core skill topics at this seniority (skips published by default)">
            <mat-icon>auto_stories</mat-icon>
            Batch generate lessons
          </button>
          <button
            mat-stroked-button
            color="warn"
            type="button"
            (click)="forceGenerateAllLessons(profile.level)"
            [disabled]="isBusy || !slug"
            title="Force regenerate lessons for all core topics (5 resources/pack; fully AI when a topic has zero resources)">
            <mat-icon>restart_alt</mat-icon>
            Force generate all lessons
          </button>
        </div>
        <div class="skills-grid">
          <ng-container *ngFor="let skillItem of profile.skills; trackBy: trackBySkill">
            <ng-container
              *ngTemplateOutlet="skillCard; context: { skillItem: skillItem, level: profile.level, specializationSlug: null }">
            </ng-container>
          </ng-container>
        </div>
      </mat-expansion-panel>
    </mat-accordion>
  </section>

  <section class="section">
    <h2>Specializations and Resources</h2>
    <mat-accordion multi>
      <mat-expansion-panel *ngFor="let specialization of engagedSpecializations(role)">
        <mat-expansion-panel-header>
          <mat-panel-title>{{ specialization.name }}</mat-panel-title>
          <mat-panel-description>{{ specialization.slug }}</mat-panel-description>
        </mat-expansion-panel-header>
        <mat-accordion multi>
          <mat-expansion-panel *ngFor="let profile of specialization.seniorityProfiles" [hidden]="!profileHasVisibleSkills(profile.skills)">
            <mat-expansion-panel-header>
              <mat-panel-title>{{ profile.level }}</mat-panel-title>
              <mat-panel-description>{{ profile.skills.length}} skills</mat-panel-description>
            </mat-expansion-panel-header>
            <div class="profile-actions">
              <button mat-stroked-button type="button" (click)="openAddSkillDialog(profile.level, specialization.slug)" [disabled]="loading">
                <mat-icon>add</mat-icon>
                Add skill
              </button>
              <button mat-stroked-button type="button" (click)="openCopySkillsDialog(profile.level, specialization.slug)" [disabled]="loading">
                <mat-icon>content_copy</mat-icon>
                Copy skills from…
              </button>
              <button
                mat-stroked-button
                type="button"
                (click)="batchGenerateLessons(profile.level, specialization.slug)"
                [disabled]="isBusy || !slug"
                title="Generate canonical lessons for this specialization's focus skill topics (skips published by default)">
                <mat-icon>auto_stories</mat-icon>
                Batch generate lessons
              </button>
              <button
                mat-stroked-button
                color="warn"
                type="button"
                (click)="forceGenerateAllLessons(profile.level, specialization.slug)"
                [disabled]="isBusy || !slug"
                title="Force regenerate lessons for all focus topics (5 resources/pack; fully AI when a topic has zero resources)">
                <mat-icon>restart_alt</mat-icon>
                Force generate all lessons
              </button>
            </div>
            <div class="skills-grid">
              <ng-container *ngFor="let skillItem of profile.skills; trackBy: trackBySkill">
                <ng-container
                  *ngTemplateOutlet="skillCard; context: { skillItem: skillItem, level: profile.level, specializationSlug: specialization.slug }">
                </ng-container>
              </ng-container>
            </div>
          </mat-expansion-panel>
        </mat-accordion>
      </mat-expansion-panel>
    </mat-accordion>
  </section>
</ng-container>
